import useUpdateProject from '@/api/mutations/project/useUpdateProject';
import { graphql } from '@/gql';
import { usePermissions } from '@/hooks/usePermissions';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import InlineSwitchField from '@/ui/components/inlineSwitchField/InlineSwitchField';
import OptionsSectionSkeleton from '@/ui/components/optionsSectionSkeleton/OptionsSectionSkeleton';
import { ResultOf } from '@graphql-typed-document-node/core';
import { AnchorButton, H4 } from '@payaca/ambrosia';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

export const ProposalPaymentOptions_Project = graphql(`
  fragment ProposalPaymentOptions_Project on Project {
    id
    config {
      allowStripePayment
      allowBankTransferPayment
    }
  }
`);

export const ProposalPaymentOptions_Account = graphql(`
  fragment ProposalPaymentOptions_Account on Account {
    id
    externalPaymentRedirectUrl
    paymentSetup {
      stripeConnected
      bankDetailsProvided
    }
  }
`);

export type TProps = {
  /** Public id of the Project whose payment options are being edited. */
  projectId: string;
  /** Project data supplying the current option values (from the page's root query). */
  project?: ResultOf<typeof ProposalPaymentOptions_Project>;
  /** Account data indicating which payment methods are set up (from the page's root query). */
  account?: ResultOf<typeof ProposalPaymentOptions_Account>;
  className?: string;
};

type FormValues = {
  allowStripePayment: boolean;
  allowBankTransferPayment: boolean;
};

/**
 * Auto-saving form of payment-method toggles (Stripe, direct bank transfer)
 * offered to the customer. Rendered only once its data has loaded, so the form
 * seeds its defaults from the persisted values.
 */
const PaymentOptionsForm: FC<{
  projectId: string;
  config: ResultOf<typeof ProposalPaymentOptions_Project>['config'];
  account: ResultOf<typeof ProposalPaymentOptions_Account>;
  className?: string;
}> = ({ projectId, config, account, className }) => {
  const { mutate: updateProject } = useUpdateProject();

  const methods = useForm<FormValues>({
    defaultValues: {
      allowStripePayment: config.allowStripePayment,
      allowBankTransferPayment: config.allowBankTransferPayment,
    },
  });

  const isConnectedToStripe = account.paymentSetup.stripeConnected;
  const hasBankDetails = account.paymentSetup.bankDetailsProvided;

  return (
    <AutoSaveForm
      methods={methods}
      className={clstx('flex flex-col gap-3', className)}
      onSubmit={(values) => updateProject({ projectId, config: values })}
    >
      <H4>Payment options</H4>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Controller<FormValues, 'allowStripePayment'>
            name="allowStripePayment"
            render={({ field }) => (
              <InlineSwitchField
                name={field.name}
                label="Stripe"
                tooltip="Enables your customer to make online card payments or bank transfers directly into your Stripe account, as well as any other payment methods you enable"
                value={field.value}
                onChange={field.onChange}
                disabled={!isConnectedToStripe}
              />
            )}
          />
          {!isConnectedToStripe && (
            <AnchorButton
              as={Link}
              to="/companySettings/integrations-data/connections"
              variant="link"
              className="ms-7"
            >
              Connect Stripe to accept online payments
            </AnchorButton>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Controller<FormValues, 'allowBankTransferPayment'>
            name="allowBankTransferPayment"
            render={({ field }) => (
              <InlineSwitchField
                name={field.name}
                label="Direct bank transfer"
                tooltip="Displays your bank details to enable a client to make a bank transfer"
                value={field.value}
                onChange={field.onChange}
                disabled={!hasBankDetails}
              />
            )}
          />
          {!hasBankDetails && (
            <AnchorButton
              as={Link}
              to="/companySettings/sales-payments/payments"
              variant="link"
              className="ms-7"
            >
              Add your bank details to accept transfers
            </AnchorButton>
          )}
        </div>
      </div>
    </AutoSaveForm>
  );
};

/**
 * Lets the user choose which payment methods a Proposal offers the customer.
 * Options the account has not set up (Stripe not connected, no bank details)
 * are disabled with a link to the relevant settings. Hidden entirely for
 * accounts that redirect payment externally, or users without permission.
 */
const ProposalPaymentOptions: FC<TProps> = (props) => {
  const { projectId, project, account, className } = props;

  const { userHasRequiredPermission } = usePermissions();
  const canManagePaymentOptions = userHasRequiredPermission([
    DealsPermissions.MANAGE_PAYMENT_OPTIONS,
  ]);

  if (!canManagePaymentOptions) return null;

  // Show a skeleton until the data behind the rendered state has settled, so we
  // never flash default values (unchecked boxes, wrong disabled/link state, or
  // the section before an external-redirect account hides it).
  const config = project?.config;
  if (!account || !config) {
    return (
      <OptionsSectionSkeleton title="Payment options" className={className} />
    );
  }

  if (account.externalPaymentRedirectUrl) return null;

  return (
    <PaymentOptionsForm
      projectId={projectId}
      config={config}
      account={account}
      className={className}
    />
  );
};

export default ProposalPaymentOptions;
